import request from '../utils/request'

/**
 * 文件上传API
 */

/**
 * 上传文件
 * @param {File} file 文件对象
 * @param {string} businessType 业务类型：CONTRACT/FOLLOW/EXPENSE/TRIP/RETURN/DEAL等
 * @param {string} module 模块名称（可选，如：合同管理/客户跟进等）
 * @param {Function} onUploadProgress 上传进度回调函数 (progressEvent) => {}
 * @returns {Promise} 上传结果
 */
export function uploadFile(file, businessType, module, onUploadProgress) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('source', 'pc')
  if (businessType) {
    formData.append('businessType', businessType)
  }
  if (module) {
    // 对 module 参数进行 URL 编码，避免中文乱码
    // 后端会进行 URL 解码和编码修复
    formData.append('module', encodeURIComponent(module))
  }
  
  return request({
    url: '/files/upload',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onUploadProgress ? (progressEvent) => {
      if (progressEvent.total > 0) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onUploadProgress(percentCompleted)
      }
    } : undefined
  })
}

/**
 * 下载文件
 * @param {string} url 文件URL
 * @param {string} fileName 文件名（可选）
 * @returns {Promise} 下载结果
 * 注意：浏览器缓存30天的设置需要在后端API的响应头中设置：Cache-Control: max-age=2592000, public
 */
export function downloadFile(url, fileName) {
  return request({
    url: '/files/download',
    method: 'get',
    params: {
      url,
      fileName
    },
    responseType: 'blob'
  })
}

/**
 * 预览文件（图片等）
 * @param {string} url 文件URL
 * @returns {string} 预览URL（通过后端代理，解决COS 403问题）
 */
export function getPreviewUrl(url) {
  if (!url) {
    console.warn('[getPreviewUrl] URL为空')
    return url
  }
  
  // 如果是blob URL，直接返回（不需要代理）
  if (url.startsWith('blob:')) {
    console.log('[getPreviewUrl] 是blob URL，直接返回:', url)
    return url
  }
  
  // 如果已经是代理URL，直接返回
  if (url.includes('/files/preview')) {
    console.log('[getPreviewUrl] 已经是代理URL，直接返回:', url)
    return url
  }
  
  // 判断是否为COS URL
  const isCosUrl = url.includes('cos.') || url.includes('myqcloud.com')
  
  console.log('[getPreviewUrl] URL转换检查:', { url, isCosUrl })
  
  // 如果是COS URL，使用后端代理接口
  if (isCosUrl) {
    // 使用后端代理接口，避免403和CORS问题
    // 从环境变量或request配置获取baseURL
    const baseUrl = process.env.VUE_APP_BASE_API || request.defaults?.baseURL || 'http://localhost:8086'
    // 确保baseURL不以斜杠结尾，url参数以斜杠开头
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
    const proxyUrl = `${cleanBaseUrl}/files/preview?url=${encodeURIComponent(url)}`
    console.log('[getPreviewUrl] COS URL转换为代理URL:', { original: url, proxy: proxyUrl })
    return proxyUrl
  }
  
  // 本地文件或非COS URL，直接返回
  console.log('[getPreviewUrl] 非COS URL，直接返回:', url)
  return url
}

/**
 * 获取文件名
 * @param {string} fileUrl 文件URL
 * @returns {Promise} 文件名
 */
export function getFileName(fileUrl) {
  return request({
    url: '/files/get-filename',
    method: 'get',
    params: { fileUrl }
  })
}

/**
 * 上传分片
 * @param {File} chunk 分片文件
 * @param {string} uploadId 上传ID
 * @param {number} chunkNumber 分片序号（从0开始）
 * @param {number} totalChunks 总分片数
 * @param {number} chunkSize 当前分片大小
 * @param {number} totalSize 文件总大小
 * @param {string} fileName 最终文件名
 * @param {string} originalFileName 原始文件名
 * @param {string} businessType 业务类型
 * @param {string} module 模块名称
 * @param {Function} onProgress 上传进度回调
 * @returns {Promise} 上传结果
 */
export function uploadChunk(chunk, uploadId, chunkNumber, totalChunks, chunkSize, totalSize, 
                            fileName, originalFileName, businessType, module, onProgress) {
  const formData = new FormData()
  formData.append('file', chunk)
  formData.append('uploadId', uploadId)
  formData.append('chunkNumber', chunkNumber.toString())
  formData.append('totalChunks', totalChunks.toString())
  formData.append('chunkSize', chunkSize.toString())
  formData.append('totalSize', totalSize.toString())
  formData.append('fileName', fileName)
  formData.append('originalFileName', originalFileName)
  formData.append('source', 'pc')
  if (businessType) {
    formData.append('businessType', businessType)
  }
  if (module) {
    // 对 module 参数进行 URL 编码，避免中文乱码
    // 后端会进行 URL 解码和编码修复
    formData.append('module', encodeURIComponent(module))
  }
  
  return request({
    url: '/files/upload-chunk',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onProgress ? (progressEvent) => {
      if (progressEvent.total > 0) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(percentCompleted)
      }
    } : undefined
  })
}

/**
 * 合并分片
 * @param {string} uploadId 上传ID
 * @returns {Promise} 合并结果
 */
export function mergeChunks(uploadId) {
  return request({
    url: '/files/merge-chunks',
    method: 'post',
    params: { uploadId }
  })
}

/**
 * 获取分片上传状态
 * @param {string} uploadId 上传ID
 * @returns {Promise} 分片状态
 */
export function getChunkStatus(uploadId) {
  return request({
    url: '/files/chunk-status',
    method: 'get',
    params: { uploadId }
  })
}

/**
 * 取消分片上传
 * @param {string} uploadId 上传ID
 * @returns {Promise} 取消结果
 */
export function cancelUpload(uploadId) {
  return request({
    url: '/files/cancel-upload',
    method: 'post',
    params: { uploadId }
  })
}

